/**
 * Utility for detecting text directionality in mixed-script content.
 *
 * Solves the BiDi rendering issue where Arabic (RTL) text containing
 * embedded LTR segments (English words, numbers, parentheses) causes
 * the Unicode BiDi algorithm to produce incorrect visual ordering.
 */

// RTL Unicode ranges: Arabic, Hebrew, Syriac, Thaana, Arabic supplements & presentation forms
const RTL_CHAR_PATTERN = /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0750-\u077F\u0780-\u07BF\u08A0-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;

// LTR Unicode ranges: Basic Latin letters, Latin Extended, Greek, Cyrillic
const LTR_CHAR_PATTERN = /[A-Za-z\u00C0-\u024F\u0370-\u03FF\u0400-\u04FF]/;

/**
 * Detects the dominant text direction of a string by analyzing its character composition.
 *
 * Uses a weighted approach: counts RTL vs LTR strong characters and returns
 * the direction of the majority. Falls back to 'auto' when no strong
 * directional characters are found (e.g. numbers-only strings).
 *
 * Args:
 *   text: The text string to analyze.
 *
 * Returns:
 *   'rtl' if predominantly right-to-left, 'ltr' if predominantly left-to-right,
 *   or 'auto' if no strong directional characters are found.
 */
export const detectTextDirection = (text) => {
    if (!text || typeof text !== 'string') return 'auto';

    let rtlCount = 0;
    let ltrCount = 0;

    for (const char of text) {
        if (RTL_CHAR_PATTERN.test(char)) {
            rtlCount++;
        } else if (LTR_CHAR_PATTERN.test(char)) {
            ltrCount++;
        }
    }

    if (rtlCount === 0 && ltrCount === 0) return 'auto';

    return rtlCount >= ltrCount ? 'rtl' : 'ltr';
};
